import type { ActionDescriptor, ActionEffect } from '@krp/client-runtime'

import { SYNC_WINDOWS as W } from '../windows/registerWindows'

/**
 * Реестр действий пакета `sync-ui` (UI-ACTIONS): что делает каждая кнопка раздела «Синхронизация» — для подсказки ⓘ. Описания —
 * словами магазина, без кодов и внутренних слов (проверяет тест реестра). Статус не пишется руками: он вычисляется из
 * доказательств, а их у первой версии раздела ещё нет — тесты и живая проверка появятся вместе со службой на стенде.
 */
const VIEW = 'Тот, кому выдано право смотреть синхронизацию.'
const APPLY = 'Тот, кому выдано право применять синхронизацию.'
const CONFIGURE = 'Тот, кому выдано право настраивать синхронизацию.'
const SAVED = 'Решение сохраняется на сервере: после обновления страницы, на другом устройстве и у других сотрудников оно на месте.'
const RELOAD = 'Только перечитывает.'

interface Entry {
  id: string
  windows: readonly string[]
  label: string
  summary: string
  effect: ActionEffect
  effectText?: string
  access?: string
  disabledWhen?: string
}

const entry = (value: Entry): ActionDescriptor => ({ ...value, evidence: {} })

export const syncActions: readonly ActionDescriptor[] = [
  // Вход раздела
  entry({
    id: 'sync.home.open-divergences',
    windows: [W.home],
    label: 'Расхождения',
    summary: 'Открывает все расхождения этого источника с остальными: что добавлено, изменено, удалено. Число на кнопке — сколько строк ждут решения.',
    effect: 'view',
    access: VIEW,
  }),
  entry({
    id: 'sync.home.open-catalog',
    windows: [W.home],
    label: 'Расхождения общего каталога',
    summary: 'Открывает позиции, которые есть только в общем каталоге и нет ни в одном источнике. Их можно добавить в источники или оставить.',
    effect: 'view',
    access: VIEW,
  }),
  entry({
    id: 'sync.home.check',
    windows: [W.home],
    label: 'Проверить сейчас',
    summary: 'Перечитывает весь источник и пересчитывает расхождения, не дожидаясь ночной сверки. Идёт на сервере — страницу можно закрыть.',
    effect: 'read-external',
    effectText: 'Читает товары, штрихкоды, цены и фото источника и обновляет расхождения в нашей базе. В источнике ничего не меняется.',
    access: APPLY,
    disabledWhen: 'Проверка уже идёт.',
  }),
  entry({
    id: 'sync.home.init',
    windows: [W.home],
    label: 'Инициализация связей',
    summary: 'Открывает найденные пары «запись источника — товар общего каталога» с одинаковым именем или штрихкодом, чтобы связать их одним нажатием.',
    effect: 'view',
    access: VIEW,
  }),
  entry({ id: 'sync.home.open-ignores', windows: [W.home], label: 'Не отслеживается', summary: 'Открывает список того, что вы перестали отслеживать, — его можно вернуть.', effect: 'view', access: VIEW }),
  entry({ id: 'sync.home.open-price-maps', windows: [W.home], label: 'Сопоставление цен', summary: 'Открывает настройку: какой вид цены одного источника соответствует какому виду цены другого.', effect: 'view', access: VIEW }),
  entry({ id: 'sync.home.open-schedule', windows: [W.home], label: 'Ночная сверка', summary: 'Открывает время, когда система раз в сутки целиком сверяет все источники.', effect: 'view', access: VIEW }),
  entry({ id: 'sync.home.open-journal', windows: [W.home], label: 'Журнал', summary: 'Открывает журнал: кто, что, куда и когда применил или перестал отслеживать.', effect: 'view', access: VIEW }),
  entry({ id: 'sync.home.reload', windows: [W.home], label: 'Повторить', summary: 'Сводка источников не пришла. Кнопка запрашивает её ещё раз.', effect: 'read', effectText: RELOAD }),

  // Окно расхождений
  entry({
    id: 'sync.divergences.filter-change',
    windows: [W.divergences],
    label: 'Что показывать',
    summary: 'Оставляет в списке только добавленное, изменённое, удалённое или то, что есть только в общем каталоге. Рядом — число строк каждого вида.',
    effect: 'view',
  }),
  entry({ id: 'sync.divergences.filter-kind', windows: [W.divergences], label: 'Вид данных', summary: 'Оставляет в списке только номенклатуру, штрихкоды, цены или фото.', effect: 'view' }),
  entry({ id: 'sync.divergences.search', windows: [W.divergences], label: 'Найти', summary: 'Оставляет в списке строки с этим названием; артикул и штрихкод ищутся целиком, точно.', effect: 'view' }),
  entry({
    id: 'sync.divergences.select-row',
    windows: [W.divergences],
    label: 'Отметить строку',
    summary: 'Отмечает строку для применения или «Не отслеживать». Отметки не сохраняются, пока вы не нажали одну из этих кнопок.',
    effect: 'view',
  }),
  entry({
    id: 'sync.divergences.select-all',
    windows: [W.divergences],
    label: 'Выбрать все на странице',
    summary: 'Отмечает все строки страницы или снимает со всех отметку. Смена страницы или фильтра снимает отметки, чтобы применение не задело строки, которых не видно.',
    effect: 'view',
    disabledWhen: 'На странице нет строк.',
  }),
  entry({
    id: 'sync.divergences.target',
    windows: [W.divergences],
    label: 'Куда применить',
    summary: 'Источники, к которым применить отмеченные строки или для которых перестать их отслеживать.',
    effect: 'view',
  }),
  entry({
    id: 'sync.divergences.apply',
    windows: [W.divergences],
    label: 'Применить…',
    summary: 'Открывает окно применения: что именно уйдёт в выбранные источники, поля для создания и цены «было — станет». Пока вы не нажали там «Применить», ничего не меняется.',
    effect: 'view',
    access: APPLY,
    disabledWhen: 'Не отмечено ни одной строки.',
  }),
  entry({
    id: 'sync.divergences.ignore-level',
    windows: [W.divergences],
    label: 'Не отслеживать: что именно',
    summary: 'Только эту запись (штрихкод, цену, фото) или весь товар по этому виду данных.',
    effect: 'view',
  }),
  entry({
    id: 'sync.divergences.ignore',
    windows: [W.divergences],
    label: 'Не отслеживать',
    summary: 'Система перестаёт показывать отмеченные строки для выбранных источников. Уровень «весь товар» — только у позиций, уже связанных с общим каталогом. Вернуть можно в списке «Не отслеживается».',
    effect: 'write-local',
    effectText: SAVED,
    access: APPLY,
    disabledWhen: 'Не отмечено ни одной строки или ни одного источника.',
  }),
  entry({ id: 'sync.divergences.page', windows: [W.divergences], label: 'Страница списка', summary: 'Переходит на предыдущую или следующую страницу расхождений.', effect: 'view', disabledWhen: 'Вы на первой или на последней странице.' }),
  entry({ id: 'sync.divergences.reload', windows: [W.divergences], label: 'Повторить', summary: 'Список расхождений не пришёл. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: RELOAD }),
  entry({ id: 'sync.divergences.apply-back', windows: [W.divergences], label: 'К списку', summary: 'Возвращает к списку расхождений. Отметки и выбранные источники остаются.', effect: 'view' }),
  entry({
    id: 'sync.divergences.apply-action',
    windows: [W.divergences],
    label: 'Что сделать',
    summary: 'Передать значение в выбранные источники, удалить там позицию, штрихкод, цену или фото, или вернуть удалённое в этом источнике. Удаление — обычное для источника: в 1С пометка удаления, в своей базе архив.',
    effect: 'view',
    disabledWhen: 'Отмеченным строкам подходит одно действие.',
  }),
  entry({
    id: 'sync.divergences.apply-from',
    windows: [W.divergences],
    label: 'Значение брать из',
    summary: 'Из какого источника взять значение, если оно разное в разных источниках.',
    effect: 'view',
    disabledWhen: 'Значение есть только в одном источнике.',
  }),
  entry({
    id: 'sync.divergences.create-field',
    windows: [W.divergences],
    label: 'Поле создания для всех',
    summary: 'Значение, с которым позиция создаётся в этом источнике, — сразу для всех отмеченных строк. Заполнено настройкой «создавать по умолчанию» источника.',
    effect: 'view',
  }),
  entry({ id: 'sync.divergences.create-row', windows: [W.divergences], label: 'Своё для строки', summary: 'Раскрывает поля создания одной строки, чтобы задать ей значения, отличные от общих.', effect: 'view' }),
  entry({ id: 'sync.divergences.create-field-row', windows: [W.divergences], label: 'Поле создания для строки', summary: 'Значение поля только для этой строки; остальные строки берут общее.', effect: 'view' }),
  entry({
    id: 'sync.divergences.price-rule',
    windows: [W.divergences],
    label: 'Правило цены',
    summary: 'Как переносить цену между источником без НДС и источником с НДС: та же цена, с надбавкой НДС или без НДС. Рядом видно «было — станет» по ставке НДС товара в источнике с НДС.',
    effect: 'view',
  }),
  entry({
    id: 'sync.divergences.price-rule-save',
    windows: [W.divergences],
    label: 'Сохранить правило без применения',
    summary: 'Запоминает правило цены для отмеченных товаров и выбранных источников, не меняя самих цен. Расхождения цен пересчитываются по нему.',
    effect: 'write-local',
    effectText: SAVED,
    access: CONFIGURE,
    disabledWhen: 'У отмеченных цен нет общего товара или не выбраны источник-образец и цели.',
  }),
  entry({
    id: 'sync.divergences.apply-confirm',
    windows: [W.divergences],
    label: 'Применить',
    summary: 'Отправляет отмеченные строки в выбранные источники фоновой задачей и открывает её ход. Для цен сначала сохраняется выбранное правило. Для созданных позиций сразу появляются связи с общим каталогом.',
    effect: 'write-external',
    effectText: 'Создаёт, меняет или удаляет позиции, штрихкоды, цены и фото в выбранных источниках. Итог по каждой строке — в окне хода и журнале.',
    access: APPLY,
    disabledWhen: 'Не выбраны источники, не задано обязательное поле создания или задача уже отправлена.',
  }),

  // Ход задачи
  entry({
    id: 'sync.job.retry',
    windows: [W.job],
    label: 'Повторить неудавшиеся',
    summary: 'Отправляет ещё раз только строки с ошибкой — новой фоновой задачей — и открывает её ход. Строку, исход которой остался неизвестен, система сначала проверяет в источнике, поэтому второй позиции не появится.',
    effect: 'write-external',
    effectText: 'Создаёт, меняет или удаляет в источниках то, что не получилось в прошлый раз. Итог по каждой строке — в окне хода и журнале.',
    access: APPLY,
    disabledWhen: 'Повтор уже отправлен.',
  }),
  entry({ id: 'sync.job.reload', windows: [W.job], label: 'Повторить', summary: 'Не удалось узнать, как идёт задача. Кнопка спрашивает сервер ещё раз.', effect: 'read', effectText: RELOAD }),

  // Инициализация связей
  entry({ id: 'sync.init.select-row', windows: [W.init], label: 'Связать', summary: 'Отмечает пару для связывания.', effect: 'view' }),
  entry({ id: 'sync.init.select-all', windows: [W.init], label: 'Выбрать все', summary: 'Отмечает все найденные пары или снимает со всех отметку.', effect: 'view' }),
  entry({
    id: 'sync.init.confirm',
    windows: [W.init],
    label: 'Инициализировать',
    summary: 'Связывает отмеченные записи источника с товарами общего каталога и показывает итог: сколько связано и что не связалось, с причиной. После этого расхождения считаются по связям.',
    effect: 'write-local',
    effectText: 'Записывает связи в нашу базу. В источнике ничего не меняется.',
    access: APPLY,
    disabledWhen: 'Не отмечено ни одной пары или команда уже отправлена.',
  }),
  entry({ id: 'sync.init.reload', windows: [W.init], label: 'Повторить', summary: 'Найденные пары не пришли. Кнопка запрашивает их ещё раз.', effect: 'read', effectText: RELOAD }),

  // Не отслеживается
  entry({ id: 'sync.ignores.source', windows: [W.ignores], label: 'Источник', summary: 'Источник, у которого перестать отслеживать вид данных целиком.', effect: 'view' }),
  entry({ id: 'sync.ignores.kind', windows: [W.ignores], label: 'Вид данных', summary: 'Что перестать отслеживать у источника: номенклатуру, штрихкоды, цены или фото.', effect: 'view' }),
  entry({
    id: 'sync.ignores.add',
    windows: [W.ignores],
    label: 'Не отслеживать',
    summary: 'Система перестаёт показывать расхождения этого вида данных у выбранного источника целиком.',
    effect: 'write-local',
    effectText: SAVED,
    access: APPLY,
    disabledWhen: 'Не выбран источник.',
  }),
  entry({
    id: 'sync.ignores.restore',
    windows: [W.ignores],
    label: 'Вернуть',
    summary: 'Снова включает эту запись, товар или вид данных в сравнение: расхождения по ним опять появятся в списке.',
    effect: 'write-local',
    effectText: SAVED,
    access: APPLY,
  }),
  entry({ id: 'sync.ignores.reload', windows: [W.ignores], label: 'Повторить', summary: 'Список не пришёл. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: RELOAD }),

  // Сопоставление видов цен
  entry({ id: 'sync.price-maps.source', windows: [W.priceMaps], label: 'Источник', summary: 'Источник, вид цены которого сопоставляется.', effect: 'view' }),
  entry({
    id: 'sync.price-maps.key',
    windows: [W.priceMaps],
    label: 'Вид цены',
    summary: 'Вид цены источника из его цен: список берётся из того, что источник отдал при последнем чтении цен.',
    effect: 'view',
    disabledWhen: 'Источник не выбран или его цены ещё не прочитаны.',
  }),
  entry({
    id: 'sync.price-maps.suggestion-add',
    windows: [W.priceMaps],
    label: 'Добавить предложение',
    summary: 'Добавляет в список пару видов цен с одинаковым названием у двух источников. Это ещё черновик: в базу список уходит кнопкой «Сохранить».',
    effect: 'view',
  }),
  entry({
    id: 'sync.price-maps.suggestions-add-all',
    windows: [W.priceMaps],
    label: 'Добавить все предложения',
    summary: 'Добавляет в список все предложенные пары с одинаковым названием. Проверьте их перед сохранением: в базу список уходит кнопкой «Сохранить».',
    effect: 'view',
  }),
  entry({
    id: 'sync.price-maps.add',
    windows: [W.priceMaps],
    label: 'Добавить',
    summary: 'Добавляет пару видов цен в список. Это ещё черновик: в базу список уходит кнопкой «Сохранить».',
    effect: 'view',
    disabledWhen: 'Не выбраны два разных источника, не выбраны виды цен или вид уже сопоставлен с этим источником.',
  }),
  entry({ id: 'sync.price-maps.remove', windows: [W.priceMaps], label: 'Убрать', summary: 'Убирает пару из списка. В базу изменение уходит кнопкой «Сохранить».', effect: 'view' }),
  entry({
    id: 'sync.price-maps.save',
    windows: [W.priceMaps],
    label: 'Сохранить',
    summary: 'Сохраняет сопоставление целиком — как оно видно в окне. Сравниваются и показываются расхождениями только сопоставленные виды цен.',
    effect: 'write-local',
    effectText: SAVED,
    access: CONFIGURE,
    disabledWhen: 'Изменений нет или сохранение уже отправлено.',
  }),
  entry({ id: 'sync.price-maps.reload', windows: [W.priceMaps], label: 'Повторить', summary: 'Сопоставление не пришло. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: RELOAD }),

  // Ночная сверка
  entry({ id: 'sync.schedule.time', windows: [W.schedule], label: 'Время сверки', summary: 'Во сколько система раз в сутки целиком сверяет все источники. Лучше время, когда касса не работает.', effect: 'view' }),
  entry({
    id: 'sync.schedule.save',
    windows: [W.schedule],
    label: 'Сохранить',
    summary: 'Сохраняет время ночной сверки. Следующая сверка пройдёт в новое время.',
    effect: 'write-local',
    effectText: SAVED,
    access: CONFIGURE,
    disabledWhen: 'Время не менялось или записано не в виде ЧЧ:ММ.',
  }),
  entry({ id: 'sync.schedule.reload', windows: [W.schedule], label: 'Повторить', summary: 'Расписание не пришло. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: RELOAD }),

  // Журнал
  entry({ id: 'sync.journal.open-job', windows: [W.journal], label: 'Вся задача', summary: 'Открывает ход и итог всей задачи, в которой было это действие, с дословными ошибками источников.', effect: 'view' }),
  entry({ id: 'sync.journal.page', windows: [W.journal], label: 'Страница журнала', summary: 'Переходит к более новым или более старым записям.', effect: 'view', disabledWhen: 'Вы на первой или на последней странице.' }),
  entry({ id: 'sync.journal.reload', windows: [W.journal], label: 'Повторить', summary: 'Журнал не пришёл. Кнопка запрашивает его ещё раз.', effect: 'read', effectText: RELOAD }),
]
